import icon from '../../util/icon'

// step-connected-zip-code.js
//
// The sensor's zip code, asked at the install spot right after "take it to its
// spot" — on enterprise-managed accounts only, where it is what the coming
// power-outage and weather alerts will be keyed on. A plain consumer account
// isn't asked: nothing on the account uses it yet.
//
// The phone is joined to the sensor's setup network here, so there is no
// internet. The pre-fill comes from the lookup setup.js made against the request
// IP at page start (while the phone was still online), and the answer rides on
// the onboarding instance until the after-setup reconnect step submits it —
// the same arrangement the pipe-placement question uses.
//
// The pre-fill is a suggestion, not an answer: an IP places a phone somewhere
// near its network's exit, which on cellular can be a city away. The field is
// always editable and the hint says where the suggestion came from.

// A zip code the API will accept. US five-digit (optionally +4) or a Canadian
// postal code; anything else is rejected before it can be stored.
const US_ZIP = /^(\d{5})(?:-?(\d{4}))?$/;
const CA_POSTAL = /^([A-Za-z]\d[A-Za-z])\s?(\d[A-Za-z]\d)$/;

export const normalizeZipCode = value => {

  const raw = String(value == null ? '' : value).trim();

  let match = raw.match(US_ZIP);

  if (match) {
    return match[2] ? `${match[1]}-${match[2]}` : match[1];
  }

  match = raw.match(CA_POSTAL);

  if (match) {
    return `${match[1]} ${match[2]}`.toUpperCase();
  }

  return '';

};

export class StepConnectedZipCode {

  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "connected_zip_code";
  title = "Sensor Location";

  // the suggestion that was put in the field, so "did they change it?" can be
  // answered when the value is stored
  suggested = '';

  template = `
  <div class="onboarding-step" data-for="connected_zip_code">

    <div class="onboarding-zip-code">

      <div class="onboarding-instruction__hero-placeholder">
        <img src="${window.site.home}/../assets/images/setup-hero-zip-location.png" alt="" />
      </div>

      <h2 class="onboarding-guide-heading">Where will this Temp Stick be?</h2>

      <p class="onboarding-guide-instruction">
        Enter the sensor's zip code to make use of future local power outage and severe weather alerts.
      </p>

      <form class="onboarding-zip-code__form" novalidate>

        <label class="onboarding-zip-code__label" for="onboarding_zip_code">Zip Code</label>

        <div class="onboarding-zip-code__field">
          <span class="onboarding-zip-code__field-icon">${icon('map-pin')}</span>
          <input
            id="onboarding_zip_code"
            type="text"
            name="zip_code"
            inputmode="numeric"
            autocomplete="postal-code"
            autocapitalize="characters"
            autocorrect="off"
            spellcheck="false"
            maxlength="10"
            placeholder="12345"
          />
        </div>

        <div class="onboarding-zip-code__hint" data-for="zip_hint"></div>

        <div class="onboarding-instruction__actions">
          <button type="submit" class="button onboarding-guide-next-btn" data-for="zip_continue" disabled>
            Continue
          </button>
        </div>

      </form>

    </div>

  </div>
  `;

  onBack = () => {
    this.onboarding.goToStep('connected_location');
  }

  onActivate = () => {

    this.onboarding.showBackButton();

    // Returning to the step (back from the pipe question or probe step) keeps
    // whatever they typed; only an empty field takes the suggestion.
    if (!String(this.$input.val() || '').trim()) {

      const location = this.onboarding.geo_location;
      const postal = location && location.postal ? normalizeZipCode(location.postal) : '';

      if (postal) {
        this.suggested = postal;
        this.$input.val(postal);
      }

    }

    this.refresh();

    // the suggestion is usually right, so don't pull the keyboard up over it;
    // an empty field is the case that needs typing
    if (!String(this.$input.val() || '').trim()) {
      setTimeout(() => this.$input.trigger('focus'), 0);
    }

  }

  bindActions = () => {

    const $step = $(this.onboarding.container).find('.onboarding-step[data-for="connected_zip_code"]');

    this.$form = $step.find('form');
    this.$input = $step.find('input[name="zip_code"]');
    this.$hint = $step.find('[data-for="zip_hint"]');
    this.$continue = $step.find('button[data-for="zip_continue"]');

    this.$input.on('input', () => {
      this.refresh();
    });

    this.$form.on('submit', e => {
      e.preventDefault();
      this.continue();
    });

  }

  currentZip () {
    return normalizeZipCode(this.$input.val());
  }

  // Where the suggestion came from: the account's saved answer from an earlier
  // setup, or the one-time IP lookup.
  suggestionSource () {
    const location = this.onboarding.geo_location;
    return location && String(location.source || '') === 'saved' ? 'saved' : 'ip';
  }

  // ...and how much to trust it, in words
  suggestionText () {
    return this.suggestionSource() === 'saved'
      ? 'Pre-filled from your last Temp Stick setup.'
      : 'Suggested from your current location.';
  }

  /**
   * The hint under the field says one of three things: where a suggestion came
   * from, that there was none, or that what's typed isn't a zip code yet. The
   * Continue button follows the third.
   */
  refresh () {

    const raw = String(this.$input.val() || '').trim();
    const zip = this.currentZip();

    this.$continue.prop('disabled', !zip);

    this.$hint.removeClass('is-error is-suggested');

    if (raw && !zip) {
      this.$hint.addClass('is-error').text('Enter a valid 5-digit zip code.');
      return;
    }

    if (zip && this.suggested && zip === this.suggested) {
      this.$hint
        .addClass('is-suggested')
        .html(`${icon('check-circle')} <span>${this.suggestionText()}</span>`);
      return;
    }

    if (!raw) {
      this.$hint.text(
        this.suggested
          ? "Enter the sensor's zip code."
          : "We couldn't detect your location. Enter the sensor's zip code."
      );
      return;
    }

    this.$hint.text('');

  }

  continue () {

    const zip = this.currentZip();

    if (!zip) {
      this.refresh();
      return;
    }

    this.onboarding.sensor_zip_code = zip;
    this.onboarding.sensor_zip_code_source = (this.suggested && zip === this.suggested)
      ? this.suggestionSource()
      : 'user';

    this.onboarding.goToStepAfterZipCode();

  }

}
